import React, { useState, useEffect } from 'react';
import { Sparkles, Save, AlertCircle, CheckCircle, Loader2, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Alert, AlertDescription } from '../../ui/alert';
import { supabase } from '../../../lib/supabaseClient';

const DEFAULT_PROMPT = `Analyze the RFQ and extract matching criteria in JSON.

Output MUST be valid JSON with EXACTLY this structure:
{
  "min_clearance_level": "NV1" | "NV2" | "TSPV" | "Baseline" | "None",
  "clearance_requirement_type": "ability_to_obtain",
  "required_skills": ["Skill 1", "Skill 2"],
  "nice_to_have_skills": ["Skill 3"],
  "target_locations": ["Canberra", "Sydney"],
  "min_experience_level": "beginner" | "intermediate" | "professional" | "expert"
}

STRICT RULES:
1) EXTRACT ALL: Do not summarise or compress. Extract every explicitly mentioned technology, service, tool, framework/standard (e.g., ISM, PSPF), control/governance concept, and certification.
2) NO INFERENCE: Do not add anything not explicitly present as a phrase in the RFQ text. Do not add "obvious" related skills.
3) SPECIFIC OVER GENERIC: If the RFQ mentions a specific item (e.g., "Azure Key Vault"), include it. Do not replace it with only "Azure".
4) SPLIT COMBINATIONS: Split "X/Y", "X or Y", comma lists, and parentheticals into separate skills (e.g., "ARM/Bicep" -> ["ARM", "Bicep"]).
5) REQUIRED VS NICE-TO-HAVE:
   - Put skills mentioned in Position Description, Technical experience, Key Duties, or Essential criteria into required_skills.
   - Put skills/certs labelled Desirable or Highly desirable into nice_to_have_skills.
6) DE-DUPE: Remove duplicates (case-insensitive).
7) EXPERIENCE LEVEL: 0-2 beginner, 3-5 intermediate, 5-8 professional, 8+ expert.
8) LOCATIONS: return from RFQ locations field exactly (no normalisation).

RFQ Details:
{{RFQ_JSON}}

Return ONLY the JSON object, no explanatory text.`;

const AISettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setMessage({ type: 'error', text: 'Not authenticated' });
        return;
      }

      const { data, error } = await supabase
        .from('recruiter_settings')
        .select('rfq_ai_prompt')
        .limit(1)
        .maybeSingle();

      if (!error && data?.rfq_ai_prompt) {
        setPrompt(data.rfq_ai_prompt);
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error);
      setMessage({ type: 'error', text: 'Failed to load AI settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Check if settings exist
      const { data: existing } = await supabase
        .from('recruiter_settings')
        .select('id')
        .limit(1)
        .maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('recruiter_settings')
          .update({ rfq_ai_prompt: prompt })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('recruiter_settings')
          .insert({ rfq_ai_prompt: prompt });

        if (error) throw error;
      }

      setMessage({ type: 'success', text: 'AI prompt saved successfully' });
    } catch (error) {
      console.error('Error saving AI prompt:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save AI prompt' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset to default prompt? This will discard your custom prompt.')) {
      setPrompt(DEFAULT_PROMPT);
      setMessage({ type: 'info', text: 'Prompt reset to default. Click Save to apply.' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">AI Configuration</h3>
        <p className="text-sm text-muted-foreground">
          Configure AI behavior for RFQ match profile generation
        </p>
      </div>

      {message && (
        <Alert className={
          message.type === 'error' ? 'border-destructive' :
          message.type === 'success' ? 'border-green-500' :
          'border-blue-500'
        }>
          {message.type === 'error' && <AlertCircle className="h-4 w-4" />}
          {message.type === 'success' && <CheckCircle className="h-4 w-4" />}
          <AlertDescription className={
            message.type === 'error' ? 'text-destructive' :
            message.type === 'success' ? 'text-green-700' :
            'text-blue-700'
          }>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>RFQ Analysis Prompt</CardTitle>
                <CardDescription>
                  Customize how AI extracts matching criteria from RFQs
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ai-prompt">AI Prompt Template</Label>
            <Textarea
              id="ai-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={20}
              className="font-mono text-xs"
              placeholder="Enter your custom AI prompt..."
            />
            <p className="text-xs text-muted-foreground">
              Use <code className="bg-muted px-1 py-0.5 rounded">{'{{RFQ_JSON}}'}</code> as a placeholder for RFQ data.
              The AI will replace this with actual RFQ details when generating profiles.
            </p>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>Important:</strong> Changes to the prompt will affect all future RFQ profile generations.
              Existing profiles will not be regenerated. The AI expects JSON output matching the schema shown in the prompt.
            </AlertDescription>
          </Alert>

          <div className="space-y-3 pt-4 border-t">
            <h4 className="text-sm font-medium">Skill Normalization</h4>
            <p className="text-xs text-muted-foreground">
              After AI extraction, skills are automatically normalized and matched against your skills catalog:
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
              <li><strong>Splitting:</strong> "ARM/Bicep" → ["ARM", "Bicep"]</li>
              <li><strong>Alias mapping:</strong> "IAC" → "Infrastructure as Code"</li>
              <li><strong>Fuzzy matching:</strong> Similar skills matched with 75% threshold</li>
              <li><strong>Pending skills:</strong> Unmatched skills created as "pending" for review</li>
            </ul>
            <p className="text-xs text-muted-foreground">
              Review pending skills in Settings → Skills Management
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Prompt
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AISettings;
